import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared";
import { Switch } from "@/components/ui/switch";
import {
  Radar,
  Globe,
  FishSymbol,
  Bug,
  Flame,
  Smartphone,
  CalendarClock,
  Loader2,
} from "lucide-react";

const MODULES = [
  { key: "real_time", label: "Real-time Protection", icon: Radar, desc: "Continuously monitors files and processes as they run." },
  { key: "web_shield", label: "Web Shield", icon: Globe, desc: "Blocks malicious websites and dangerous downloads." },
  { key: "phishing_guard", label: "Phishing Guard", icon: FishSymbol, desc: "Detects credential-harvesting and scam pages." },
  { key: "malware_engine", label: "Malware Engine", icon: Bug, desc: "Signature + heuristic detection of trojans & spyware." },
  { key: "firewall", label: "Firewall", icon: Flame, desc: "Controls inbound and outbound network traffic." },
  { key: "mobile_protection", label: "Mobile Protection", icon: Smartphone, desc: "Extends protection to your connected phone." },
];

export default function Protection() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [schedule, setSchedule] = useState(null);

  useEffect(() => {
    api.get("/protection").then((r) => setState(r.data));
    api.get("/schedule").then((r) => setSchedule(r.data));
  }, []);

  const saveSchedule = async (frequency, scan_type) => {
    const prev = schedule;
    setSchedule((s) => ({ ...s, frequency, scan_type }));
    try {
      const { data } = await api.post("/schedule", { frequency, scan_type });
      setSchedule(data);
      toast.success(frequency === "off" ? "Auto-scan disabled" : `Auto-scan set to ${frequency}`);
    } catch (e) {
      setSchedule(prev);
      toast.error("Failed to update schedule");
    }
  };

  const toggle = async (key, enabled) => {
    setBusy(key);
    setState((s) => ({ ...s, [key]: enabled }));
    try {
      const { data } = await api.post("/protection/toggle", { key, enabled });
      setState(data);
      const label = MODULES.find((m) => m.key === key).label;
      toast[enabled ? "success" : "warning"](`${label} ${enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      setState((s) => ({ ...s, [key]: !enabled }));
      toast.error("Failed to update module");
    } finally {
      setBusy("");
    }
  };

  if (!state)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" strokeWidth={1.5} />
      </div>
    );

  const activeCount = MODULES.filter((m) => state[m.key]).length;

  return (
    <div className="p-6 md:p-10 max-w-4xl">
      <PageHeader
        tag="Defense Grid"
        title="Protection Modules"
        subtitle="Toggle the security layers guarding your systems. More active modules mean a higher security score."
        right={
          <div className="font-mono text-sm border border-primary/50 bg-primary/10 text-primary px-4 py-2">
            {activeCount}/{MODULES.length} active
          </div>
        }
      />

      <div className="mb-6 bg-card border border-border p-6" data-testid="schedule-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 shrink-0 flex items-center justify-center border border-primary/50 bg-primary/10 text-primary">
            <CalendarClock size={22} strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-mono font-bold tracking-tight">Scheduled Auto-Scans</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Run automatic background scans and log results. {schedule?.next_run && schedule.frequency !== "off" && (
                <span className="text-primary font-mono">Next: {new Date(schedule.next_run).toLocaleString()}</span>
              )}
            </p>

            <div className="mt-4 flex flex-wrap gap-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Frequency</div>
                <div className="flex gap-2">
                  {["off", "daily", "weekly"].map((f) => (
                    <button
                      key={f}
                      data-testid={`schedule-${f}`}
                      onClick={() => saveSchedule(f, schedule?.scan_type || "quick")}
                      className={`px-4 py-2 border font-mono text-xs uppercase tracking-widest transition-colors duration-200 ${
                        schedule?.frequency === f
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Scan type</div>
                <div className="flex gap-2">
                  {["quick", "full", "mobile"].map((t) => (
                    <button
                      key={t}
                      data-testid={`schedule-type-${t}`}
                      disabled={schedule?.frequency === "off"}
                      onClick={() => saveSchedule(schedule?.frequency || "daily", t)}
                      className={`px-4 py-2 border font-mono text-xs uppercase tracking-widest transition-colors duration-200 disabled:opacity-40 ${
                        schedule?.scan_type === t
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
        {MODULES.map((m) => {
          const Icon = m.icon;
          const on = state[m.key];
          return (
            <div key={m.key} className={`bg-card p-6 transition-colors duration-200 ${on ? "" : "opacity-70"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className={`w-11 h-11 flex items-center justify-center border ${on ? "border-primary/50 bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <Switch
                  data-testid={`toggle-${m.key}`}
                  checked={on}
                  disabled={busy === m.key}
                  onCheckedChange={(v) => toggle(m.key, v)}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
              <div className="mt-4 flex items-center gap-2">
                <h3 className="font-mono font-bold tracking-tight">{m.label}</h3>
                <span className={`font-mono text-[10px] uppercase tracking-widest ${on ? "text-primary" : "text-muted-foreground"}`}>
                  {on ? "on" : "off"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{m.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
